import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../context/LanguageContext';
import { FiThumbsUp, FiEye, FiPlay, FiStar, FiRefreshCw, FiArrowRight, FiPlus, FiCalendar } from 'react-icons/fi';
import { Compass } from 'lucide-react';
import { getFallbackCover } from '../utils/defaultAvatar';
import { coverUrl, coverFallback } from '../utils/coverUrl';
import './Home.css';
import '../components/Skeleton.css';

const API = '/api';

export default function Home() {
  const getHeaders = useAuthStore(s => s.getHeaders);
  const user = useAuthStore(s => s.user);
  const { t } = useLanguage();
  const location = useLocation();
  const [recentPosts, setRecentPosts] = useState([]);
  const [publishedWorks, setPublishedWorks] = useState([]);
  const [recommendationBatch, setRecommendationBatch] = useState(0);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState({}); // 记录用户点赞的帖子
  const [dailyPick, setDailyPick] = useState(null); // 运营在后台手选的每日精选（每天最多 1 个）

  // 点赞处理函数
  const handleLike = async (postId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    try {
      const res = await axios.post(`${API}/community/posts/${postId}/like`, {}, { headers: getHeaders() });
      setLikedPosts(prev => ({ ...prev, [postId]: res.data.liked }));
      setRecentPosts(posts => (posts || []).map(p => 
        p.id === postId ? { ...p, likes: res.data.likes } : p
      ));
    } catch (err) {
      console.error('点赞失败', err);
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);

    axios.get(`${API}/maps/recommended`).then(res => {
      if (!active) return;
      setPublishedWorks(Array.isArray(res.data) ? res.data : []);
      setRecommendationBatch(0);
    }).catch(() => {
      if (active) setPublishedWorks([]);
    }).finally(() => {
      if (active) setLoading(false);
    });

    axios.get(`${API}/maps/daily-picks`).then(res => {
      if (!active) return;
      const picks = Array.isArray(res.data?.picks) ? res.data.picks : [];
      setDailyPick(picks[0] || null);
    }).catch(() => {
      if (active) setDailyPick(null);
    });

    axios.get(`${API}/community/posts`).then(res => {
      if (!active) return;
      const posts = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.posts) ? res.data.posts : []);
      setRecentPosts(posts.slice(0, 5));
    }).catch(() => {});

    return () => { active = false; };
  }, []);

  const rotationStart = publishedWorks.length > 4 ? (recommendationBatch * 4) % publishedWorks.length : 0;
  const visibleWorks = publishedWorks.length > 4
    ? Array.from({ length: 4 }, (_, index) => publishedWorks[(rotationStart + index) % publishedWorks.length])
    : publishedWorks;
  const spotlight = visibleWorks[0];
  const sideWorks = visibleWorks.slice(1);
  // 每日推荐只展示后台当天手选的平台精选；未选择时不展示，也不自动补位。
  const dailyWork = dailyPick;
  const dailyDateLabel = new Date().toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
  const coverError = (event, title) => {
    const el = event.currentTarget;
    const current = el.getAttribute('src') || '';
    const full = coverFallback(current);
    if (!el.dataset.retriedFull && full && full !== current) {
      el.dataset.retriedFull = '1';
      el.src = full;
      return;
    }
    el.onerror = null;
    el.src = getFallbackCover(title, 800, 450);
  };
  const formatCount = value => {
    const count = Number(value) || 0;
    return count >= 10000 ? `${(count / 10000).toFixed(count >= 100000 ? 0 : 1)}万` : String(count);
  };
  const recommendationReason = value => ({
    '值得关注': t('home.reasonFeatured'),
    '新发布': t('home.reasonNew'),
    '大家都在玩': t('home.reasonPopular'),
    '口碑推荐': t('home.reasonRated'),
  }[value] || value);

  if (loading) return (
    <div className="container">
      <section className="home-section home-recommend-section">
        <div className="section-header">
          <span className="sk" style={{ width: 150, height: 29 }} />
          <span className="sk" style={{ width: 52, height: 13 }} />
        </div>
        <div className="home-recommend-skeleton">
          <span className="sk home-recommend-skeleton-main" />
          <div className="home-recommend-skeleton-side">
            {[0, 1, 2].map(i => <span className="sk" key={i} />)}
          </div>
        </div>
      </section>
      <section className="home-section">
        <div className="section-header">
          <span className="sk" style={{ width: 112, height: 29 }} />
          <span className="sk" style={{ width: 52, height: 13 }} />
        </div>
        <div className="recent-posts-list">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="recent-post-item" style={{ pointerEvents: 'none' }}>
              <div className="post-left">
                <span className="sk" style={{ width: 56, height: 24, borderRadius: 12, flexShrink: 0 }} />
                <span className="sk" style={{ width: i % 2 ? '45%' : '65%', height: 14 }} />
              </div>
              <div className="post-stats">
                <span className="sk" style={{ width: 28, height: 40 }} />
                <span className="sk" style={{ width: 28, height: 40 }} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  return (
    <div className="container">

        {dailyWork && (
          <Link to={`/maps/${dailyWork.id}`} className="home-daily-card is-pick">
            <div className="home-daily-copy">
              <span className="home-pick-badge"><FiStar /> 平台精选</span>
              <div className="home-daily-eyebrow">
                <FiCalendar /> {t('home.dailyEyebrow', { date: dailyDateLabel })}
              </div>
              <h2>{dailyWork.title}</h2>
              <p>{dailyWork.description || t('home.defaultWorkDescription')}</p>
              <div className="home-daily-foot">
                <span>平台今日精选，过来看看</span>
                <span className="home-daily-cta">{t('home.dailyCta')} <FiArrowRight /></span>
              </div>
            </div>
            <img
              className="home-daily-cover"
              src={coverUrl(dailyWork.cover, 'thumb') || getFallbackCover(dailyWork.title, 480, 270)}
              alt={dailyWork.title}
              loading="eager"
              decoding="async"
              onError={event => coverError(event, dailyWork.title)}
            />
          </Link>
        )}

        <section className="home-section home-recommend-section" aria-labelledby="home-recommend-title">
          <div className="section-header home-recommend-header">
            <div>
              <h2 className="section-title" id="home-recommend-title">{t('home.recommendedWorks')}</h2>
              <p className="home-recommend-subtitle">{t('home.recommendedSubtitle')}</p>
            </div>
            <div className="home-recommend-actions">
              {publishedWorks.length > 4 && (
                <button type="button" className="home-recommend-refresh" onClick={() => setRecommendationBatch(batch => batch + 1)}>
                  <FiRefreshCw /> {t('home.refreshBatch')}
                </button>
              )}
              <Link to="/maps" className="section-more">{t('home.viewAllWorks')} <FiArrowRight /></Link>
            </div>
          </div>

          {spotlight ? (
            <div className={`home-recommend-layout ${sideWorks.length === 0 ? 'is-single' : ''}`}>
              <Link to={`/maps/${spotlight.id}`} className="home-work-spotlight" aria-label={t('home.viewWorkAria', { title: spotlight.title })}>
                <div className="home-work-spotlight-cover">
                  <img
                    src={coverUrl(spotlight.cover, 'mid') || getFallbackCover(spotlight.title, 960, 540)}
                    alt={spotlight.title}
                    loading="eager"
                    decoding="async"
                    onError={event => coverError(event, spotlight.title)}
                  />
                  <span className="home-work-reason">{recommendationReason(spotlight.recommendationReason)}</span>
                </div>
                <div className="home-work-spotlight-body">
                  <div className="home-work-labels">
                    <span>{spotlight.genre}</span>
                    <span>{spotlight.editor_mode === 'code' ? t('home.codeWork') : t('home.threeDWork')}</span>
                  </div>
                  <h3>{spotlight.title}</h3>
                  <p className="home-work-description">{spotlight.description || t('home.defaultWorkDescription')}</p>
                  <div className="home-work-spotlight-footer">
                    <div>
                      <span className="home-work-author">{spotlight.username}</span>
                      <span className="home-work-metrics">
                        <span><FiEye /> {formatCount(spotlight.views)}</span>
                        <span><FiPlay /> {t('home.peoplePlayed', { count: formatCount(spotlight.playUserCount) })}</span>
                        {spotlight.reviewCount > 0 && <span><FiStar /> {spotlight.avgRating}</span>}
                      </span>
                    </div>
                    <span className="home-work-enter">{t('home.viewWork')} <FiArrowRight /></span>
                  </div>
                </div>
              </Link>

              {sideWorks.length > 0 && (
                <div className="home-work-side-list">
                  {sideWorks.map(work => (
                    <Link to={`/maps/${work.id}`} className="home-work-side-card" key={work.id} aria-label={t('home.viewWorkAria', { title: work.title })}>
                      <img
                        src={coverUrl(work.cover, 'thumb') || getFallbackCover(work.title, 400, 260)}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={event => coverError(event, work.title)}
                      />
                      <div className="home-work-side-content">
                        <div className="home-work-side-label"><span>{recommendationReason(work.recommendationReason)}</span>{work.genre}</div>
                        <h3>{work.title}</h3>
                        <p>{work.username}</p>
                        <div className="home-work-side-meta">
                          <span><FiEye /> {formatCount(work.views)}</span>
                          <span><FiPlay /> {formatCount(work.playUserCount)}</span>
                          {work.reviewCount > 0 ? <span><FiStar /> {work.avgRating}</span> : <span>{t('home.awaitingReview')}</span>}
                        </div>
                      </div>
                      <FiArrowRight className="home-work-side-arrow" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="home-recommend-empty">
              <div className="home-recommend-empty-icon"><Compass size={30} strokeWidth={1.6} /></div>
              <div className="home-recommend-empty-copy">
                <h3>{t('home.emptyTitle')}</h3>
                <p>{t('home.emptyBody')}</p>
              </div>
              <div className="home-recommend-empty-actions">
                <Link to="/maps/new" className="home-recommend-create"><FiPlus /> {t('home.startCreating')}</Link>
                <Link to="/maps" className="home-recommend-browse">{t('home.browseWorkshop')} <FiArrowRight /></Link>
              </div>
            </div>
          )}
        </section>

        {/* 热门论坛帖子 */}
        <section className="home-section">
          <div className="section-header">
            <h2 className="section-title">{t('hotTopics')}</h2>
            <Link to="/community" className="section-more">{t('enterCommunity')}</Link>
          </div>
          <div className="recent-posts-list">
            {(recentPosts || []).map(p => (
              <Link key={p.id} to={`/community/post/${p.id}`} state={{ from: 'home' }} className="recent-post-item">
                <div className="post-left">
                  <span className="post-category-tag">{p.category}</span>
                  <span className="post-title">{p.title}</span>
                </div>
                <div className="post-stats">
                  <span 
                    className={`like-btn ${likedPosts[p.id] ? 'liked' : ''}`}
                    onClick={(e) => handleLike(p.id, e)}
                    title={user ? t('like') : t('pleaseLogin')}
                  >
                    <FiThumbsUp style={{color: likedPosts[p.id] ? '#ff6b6b' : 'inherit'}} /> {p.likes || 0}
                  </span>
                  <span>{p.comments || 0}</span>
                </div>
              </Link>
            ))}
            {recentPosts.length === 0 && (
              <p className="empty-hint">{t('noPostsYet')}</p>
            )}
          </div>
        </section>
      </div>
  );
}
